import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { registerRoutingHook, removeRoutingHook } from '@adornis/router/client/routing-state-signal.js';
import { html, nothing } from 'lit';
import { keyed } from 'lit/directives/keyed.js';
import { confirmDialog } from '../confirm-dialog.js';
import { sdrRouter } from '../sdr-router.js';
import { toast } from '../toast.js';
import type { ParsedDocument } from '../wizard/document-parser.js';
import type { DocumentUploadState } from '../wizard/document-upload.js';
import type { CampaignDraft, SequenceStep } from '../wizard/types.js';
import {
  channelsRequiringAccount,
  connectStepCarriesANote,
  defaultLinkedinSequence,
  deriveCampaignType,
  emptyCampaignDraft,
  isPlaceholderSequence,
  previewSenderId,
  salesRepsWithoutAnyUsableAccount,
} from '../wizard/types.js';
import { ConnectionNoteNotice } from '../connection-note-notice.js';
import { hasRepSetChange, senderAccountApplyAction, shouldAutoApplySalesReps } from '../wizard/sender-account-plan.js';
import { WizCampaignType } from '../wizard/wiz-campaign-type.js';
import { WizLeads } from '../wizard/wiz-leads.js';
import { WizManageLeads } from '../wizard/wiz-manage-leads.js';
import { WizOffering } from '../wizard/wiz-offering.js';
import { WizSchedule } from '../wizard/wiz-schedule.js';
import { LINKEDIN_CONNECT_NOTE_MAX_CHARS, WizSequence } from '../wizard/wiz-sequence.js';
import { WizSummary } from '../wizard/wiz-summary.js';

/** Guard function called by the routing hook before leaving the wizard. */
export let wizardBeforeLeave: (() => Promise<boolean>) | null = null;

/** Tracks an in-flight leave attempt so concurrent calls share the same modal. */
let wizardLeaveInFlight: Promise<boolean> | null = null;

/**
 * Serializes draft saves so a burst of autosaves (or autosave + manual save)
 * can never create two campaigns. The very first save POSTs a new campaign;
 * `wizardCreatedId` remembers the returned id so every later save is a PUT.
 */
let wizardSaveInFlight: Promise<string | null> | null = null;
let wizardCreatedId: string | null = null;

const STEPS = [
  { key: 'leads', label: 'Leads', icon: 'group' },
  { key: 'offering', label: 'Offering', icon: 'storefront' },
  { key: 'schedule', label: 'Schedule', icon: 'schedule' },
  { key: 'type', label: 'Sales Representatives', icon: 'support-agent' },
  { key: 'sequence', label: 'Sequence', icon: 'account-tree' },
  { key: 'summary', label: 'Summary', icon: 'summarize' },
] as const;

export type WizardStepKey = (typeof STEPS)[number]['key'];

/** Check whether a wizard step is complete based on current draft data. */
const isStepComplete = (stepKey: WizardStepKey, d: CampaignDraft, isFinished = false): boolean => {
  switch (stepKey) {
    case 'type': {
      if (!d.aiEmployeeId) return false;
      // Sales Rep shares must total exactly 100%
      const totalPct = d.aiEmployeeAssignments.reduce((s, a) => s + (a.percentage || 0), 0);
      if (d.aiEmployeeAssignments.length === 0 || totalPct !== 100) return false;
      // We don't know yet which channels the user will pick in the Sequence
      // step, so the only hard requirement here is that *some* account is
      // configured — either email or LinkedIn. Per-channel completeness is
      // re-validated on the Launch step against the actual sequence.
      return d.emailAccounts.length > 0 || d.linkedinAccountIds.length > 0;
    }
    case 'offering':
      // Only the product/service name is required. Description and talking
      // points add context but are optional, so the step is "done" as soon as
      // the name is filled in (matches stepBlockReason's gate).
      return !!d.offering.name.trim();
    case 'leads':
      return (d.leads.length > 0 || !!d.apolloSearchInput) && !!d.name.trim();
    case 'schedule':
      return d.schedule.sendDays.length > 0;
    case 'sequence':
      return d.sequence.length > 0;
    case 'summary':
      return isFinished;
  }
};

/** Parse a time value that may be 'HH:mm' or an ISO string into a Date. */
const parseTimeValue = (val: unknown): Date => {
  if (typeof val === 'string' && /^\d{1,2}:\d{2}$/.test(val)) {
    const [h, m] = val.split(':').map(Number);
    const d = new Date();
    d.setHours(h!, m!, 0, 0);
    return d;
  }
  const d = new Date(val as string);
  return isNaN(d.getTime()) ? new Date() : d;
};

/**
 * Format a schedule time into a canonical wall-clock "HH:mm" string.
 * Accepts a Date (from the time picker, in browser-local wall-clock terms) or a
 * legacy "HH:mm"/ISO string. Reading the local hours preserves exactly what the
 * user typed; the value is interpreted in the campaign's IANA timezone
 * (DST-aware) downstream. We must NOT let JSON.stringify emit a UTC ISO string,
 * which would bake in the browser's offset and shift the send window.
 */
const formatTimeHHmm = (val: Date | string): string => {
  const d = typeof val === 'string' ? parseTimeValue(val) : val;
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** Save campaign draft to API. Returns the saved _id. Throws when the server
 * rejects the save so callers can surface the failure to the user. */
const saveDraftToApi = async (draft: CampaignDraft): Promise<string | null> => {
  // Always overwrite the persisted campaignType with the value derived from
  // the current sequence — there is no longer a user-facing picker for it.
  const campaignType = deriveCampaignType(draft.sequence);
  // Serialize schedule send-window times as canonical wall-clock "HH:mm" strings.
  // The raw Date would JSON-encode to a UTC ISO string (baking in the browser's
  // offset), which the backend then misreads with server-local time — breaking
  // DST correctness for zones like Europe/Berlin.
  const scheduleForApi = {
    ...draft.schedule,
    startTime: formatTimeHHmm(draft.schedule.startTime),
    endTime: formatTimeHHmm(draft.schedule.endTime),
  };
  if (draft._id) {
    // Past 'draft', the whole Sales Rep block — who is assigned, at what share,
    // and which sending accounts each of them uses — is owned by the dedicated
    // /sales-reps endpoint (it re-keys leads, regenerates messages and
    // validates channel coverage in one atomic write). Autosaving any part of
    // it here would desync assignments from the actual leads, and worse: the
    // account buckets are pruned locally the moment a rep is unchecked, so an
    // autosave landing between "unchecked rep A" and "applied rep B" would
    // silently wipe A's accounts server-side while A is still the live
    // assignment — a campaign that then launches with no mailbox to send from.
    const ownedBySalesRepApply = !!draft.status && draft.status !== 'draft';
    const resp = await fetch(`/api/campaigns/${encodeURIComponent(draft._id)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: draft.name || undefined,
        campaignType,
        offering: draft.offering,
        schedule: scheduleForApi,
        sequence: draft.sequence,
        llmModel: draft.llmModel,
        ...(ownedBySalesRepApply
          ? {}
          : {
              aiEmployeeId: draft.aiEmployeeId,
              aiEmployeeAssignments: draft.aiEmployeeAssignments,
              emailAccounts: draft.emailAccounts,
              linkedinAccountIds: draft.linkedinAccountIds,
              emailAccountsByAie: Object.entries(draft.emailAccountsByAie).map(([aiEmployeeId, emailAccounts]) => ({
                aiEmployeeId,
                emailAccounts,
              })),
              linkedinAccountIdsByAie: Object.entries(draft.linkedinAccountIdsByAie).map(
                ([aiEmployeeId, linkedinAccountIds]) => ({ aiEmployeeId, linkedinAccountIds }),
              ),
            }),
        autoReply: draft.autoReply,
        replyPrompt: draft.replyPrompt,
        replyLlmModel: draft.replyLlmModel,
        crmAutoSync: draft.crmAutoSync,
        language: draft.language || undefined,
        apolloSearchInput: draft.apolloSearchInput,
        projectId: draft.projectId,
      }),
    });
    if (!resp.ok) throw new Error(`Update failed (HTTP ${resp.status})`);
    // Save leads separately via the leads endpoint. Once the campaign has left
    // 'draft' (generating/ready_to_launch/preparing_to_launch/active/...),
    // leads/messages carry real progress — the leads endpoint destructively
    // replaces every lead (fresh _ids, status reset to 'pending'), which would
    // wipe that progress and orphan every job/message referencing the old lead
    // ids. The server refuses this too (409, gated on status not just
    // externalCampaignId); we skip the call entirely here so opening a
    // non-draft campaign in the editor can't silently corrupt it, and doesn't
    // need to surface a 409 as an error. `status === undefined` covers a
    // brand-new draft that was just created by this same save and hasn't been
    // re-fetched yet — always safe.
    if (
      draft.leads.length > 0 &&
      !draft.externalCampaignId &&
      (draft.status === undefined || draft.status === 'draft')
    ) {
      const leadsResp = await fetch(`/api/campaigns/${encodeURIComponent(draft._id)}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leads: draft.leads }),
      });
      if (!leadsResp.ok) throw new Error(`Saving leads failed (HTTP ${leadsResp.status})`);
    }
    return draft._id;
  } else {
    const resp = await fetch('/api/campaigns', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: draft.name || `Draft - ${draft.offering.name || 'Untitled'} - ${new Date().toISOString().slice(0, 10)}`,
        campaignType,
        offering: draft.offering,
        schedule: scheduleForApi,
        sequence: draft.sequence,
        emailAccounts: draft.emailAccounts,
        linkedinAccountIds: draft.linkedinAccountIds,
        llmModel: draft.llmModel,
        aiEmployeeId: draft.aiEmployeeId,
        aiEmployeeAssignments: draft.aiEmployeeAssignments,
        emailAccountsByAie: Object.entries(draft.emailAccountsByAie).map(([aiEmployeeId, emailAccounts]) => ({
          aiEmployeeId,
          emailAccounts,
        })),
        linkedinAccountIdsByAie: Object.entries(draft.linkedinAccountIdsByAie).map(
          ([aiEmployeeId, linkedinAccountIds]) => ({ aiEmployeeId, linkedinAccountIds }),
        ),
        autoReply: draft.autoReply,
        replyPrompt: draft.replyPrompt,
        replyLlmModel: draft.replyLlmModel,
        crmAutoSync: draft.crmAutoSync,
        language: draft.language || undefined,
        apolloSearchInput: draft.apolloSearchInput,
        projectId: draft.projectId,
      }),
    });
    const data = await resp.json().catch(() => null);
    if (!resp.ok || !data?.ok) throw new Error(data?.error || `Create failed (HTTP ${resp.status})`);
    const savedId = data.campaign?._id ? String(data.campaign._id) : null;
    // Save leads for the newly created campaign
    if (savedId && draft.leads.length > 0) {
      const leadsResp = await fetch(`/api/campaigns/${encodeURIComponent(savedId)}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leads: draft.leads }),
      });
      if (!leadsResp.ok) throw new Error(`Saving leads failed (HTTP ${leadsResp.status})`);
    }
    return savedId;
  }
};

/**
 * The Sales Rep step's "no sending account picked yet" gate — the one step
 * gate that stays quiet until the user actually tries to move on.
 *
 * Switching the Sales Rep on a cooling-down campaign used to greet you with a
 * warning banner the instant you ticked the new rep, and then an error when
 * you carried on — two messages for one thing you were already on your way to
 * doing. Now the rep picker carries a single quiet inline hint on the rep
 * that's missing an account, and this sentence is what you get if you push
 * forward anyway. Compared by identity in the render below to keep the loud
 * bottom banner (correct for every other gate) off this one.
 */
const SALES_REP_ACCOUNT_BLOCK = 'Please select an email or LinkedIn account.';

export const CampaignWizard = stateful(({ campaignId }: { campaignId?: string }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [draft, setDraft] = useState<CampaignDraft>(emptyCampaignDraft());
  // Stable ref to the latest draft for ASYNC writers (the sequence step
  // persists generated samples once the LLM responds — possibly after the
  // user already navigated to another wizard step). Merging into a
  // render-time `draft` closure there would wipe edits made in between.
  const [liveDraft] = useState({ current: draft });
  liveDraft.current = draft;
  // Editing an existing campaign that already left 'draft' (ready_to_launch /
  // preparing_to_launch — the only statuses the campaign-detail page's Edit
  // button is shown for besides 'draft' itself). Leads/messages already exist
  // for these, so several steps behave differently: Leads becomes read-only,
  // adding a sequence step routes through the dedicated endpoint (which
  // generates messages for it) instead of relying on a from-scratch prepare,
  // Sales Rep changes route through the dedicated regenerate-aware endpoint,
  // and the final step skips /api/campaign/prepare entirely (see below).
  const isEditingNonDraft = !!draft.status && draft.status !== 'draft';
  // Step ids that already exist server-side with generated messages (snapshot
  // taken when an existing campaign loads — see the load effect below). Used
  // in edit mode to tell "a step the user just added, still being typed into"
  // apart from "a step that already has messages and can't be removed here."
  const [confirmedStepIds] = useState({ current: new Set<string>() });
  // Server-side truth of the Sales Rep assignments (snapshotted at load,
  // advanced on every successful /sales-reps apply). While editing a non-draft
  // campaign the draft may run ahead of it — the diff drives the
  // "Apply changes" bar in the Sales Rep step.
  const [appliedAssignments] = useState<{ current: CampaignDraft['aiEmployeeAssignments'] }>({ current: [] });
  // Same idea for the per-rep sending accounts: past 'draft' they travel with
  // the assignment through /sales-reps (never through the autosave), so the
  // Apply bar has to diff them too — otherwise picking the new rep's account
  // would look like an unsaved change that nothing ever saves.
  const [appliedEmailAccountsByAie] = useState<{ current: Record<string, string[]> }>({ current: {} });
  const [appliedLinkedinAccountIdsByAie] = useState<{ current: Record<string, string[]> }>({ current: {} });
  const [applyingReps, setApplyingReps] = useState(false);
  // Debounce timer for auto-applying Sales-Rep / sender-account changes in edit
  // mode. Past 'draft' the rep block does NOT ride the plain autosave
  // (saveDraftToApi omits it — see there); this is what makes a rep change feel
  // like every other field instead of waiting on an "Apply" button the user
  // could forget (LinkedIn-UAT 4 Sep): it commits itself once the picker settles
  // into a valid, applicable state. Also re-used for a short retry when the
  // dedicated endpoint is briefly busy regenerating an earlier edit.
  const [pendingRepsTimer] = useState({ current: null as ReturnType<typeof setTimeout> | null });
  // Debounce timers for not-yet-confirmed new steps, keyed by step id — mirrors
  // the 1.5s autosave debounce so the create-step endpoint isn't hit on every
  // keystroke while the user is still typing the step's prompt.
  const [pendingStepTimers] = useState(new Map<string, ReturnType<typeof setTimeout>>());
  // Debounce timer for Offering edits in edit mode — mirrors pendingStepTimers.
  const [pendingOfferingTimer] = useState({ current: null as ReturnType<typeof setTimeout> | null });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  // Track whether this draft is persisted in the database
  const [persisted, setPersisted] = useState(!!campaignId);
  // Track the furthest step the user has navigated to.
  // A step only shows a green checkmark if the user has advanced past it.
  const [maxStepReached, setMaxStepReached] = useState(campaignId ? STEPS.length - 1 : 0);
  // Whether the Sales Rep step may show its per-rep "missing account" hints yet.
  // Stays false until the user actually tries to advance past a missing-account
  // gate, so a rep just ticked is never marked before they can pick an account
  // (LinkedIn-UAT follow-up 2026-09-19). Reset on every rep-set change so a
  // freshly added/removed rep starts quiet again. Display only — the real gate
  // (stepBlockReason / salesRepApplyGate) is unaffected.
  const [revealRepGaps, setRevealRepGaps] = useState(false);
  // Track whether the campaign was successfully finished — skip save-on-leave.
  const [finished, setFinished] = useState(false);
  // True while the Offering step is parsing a document/website or the AI is
  // analyzing — navigation is blocked so the in-progress result isn't lost.
  const [offeringBusy, setOfferingBusy] = useState(false);
  // Timestamp of the last successful save — drives the visible save indicator.
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(campaignId ? Date.now() : null);
  // Edit-mode only: some edits (Sales-Rep move, step-content edit, offering
  // change) DEFER their message regeneration to wizard exit. A toast per edit
  // stacked up as the user made several changes (UAT 4 Sep). Instead we keep ONE
  // quiet, in-place indicator that a regeneration is pending, and fire the single
  // bundled toast on exit (flushPendingRegeneration). Reset once flushed.
  const [regenPending, setRegenPending] = useState(false);
  // Offering upload working-set, lifted out of the step component so fetched
  // websites / parsed documents survive step navigation. The step content is
  // re-mounted via `keyed`, which would otherwise discard this in-memory state.
  const [offeringUploadState, setOfferingUploadState] = useState<DocumentUploadState>({
    files: [],
    websites: [],
    cloudFiles: [],
  });
  const [offeringParsedDocs, setOfferingParsedDocs] = useState<ParsedDocument[]>([]);

  /** Returns true if the user has entered any meaningful data in the wizard. */
  const hasMeaningfulData = (): boolean => {
    return !!(
      draft.name ||
      draft.campaignType ||
      draft.offering.name ||
      draft.offering.description ||
      draft.leads.length > 0 ||
      draft.sequence.length > 0 ||
      draft.emailAccounts.length > 0 ||
      draft.apolloSearchInput
    );
  };

  /**
   * Stricter than `hasMeaningfulData`: a brand-new draft already has a default
   * sequence + campaignType, which we do NOT want to persist on its own. Only
   * real user input (name, leads, offering, accounts, …) makes a draft worth
   * auto-creating in the database.
   */
  const hasSaveworthyData = (): boolean => {
    return !!(
      draft.name.trim() ||
      draft.leads.length > 0 ||
      draft.offering.name.trim() ||
      draft.offering.description.trim() ||
      draft.offering.talkingPoints.length > 0 ||
      draft.aiEmployeeAssignments.length > 0 ||
      draft.emailAccounts.length > 0 ||
      draft.linkedinAccountIds.length > 0 ||
      draft.apolloSearchInput
    );
  };

  /**
   * Persist the draft (create on first save, update afterwards). All saves go
   * through here so concurrent calls are serialized and a new campaign is only
   * ever created once (see `wizardSaveInFlight` / `wizardCreatedId`).
   */
  const persistDraft = async (d: CampaignDraft): Promise<string | null> => {
    if (wizardSaveInFlight) {
      try {
        await wizardSaveInFlight;
      } catch {
        /* ignore — start our own save regardless */
      }
    }
    const effectiveId = d._id ?? wizardCreatedId ?? undefined;
    const run = saveDraftToApi(effectiveId ? { ...d, _id: effectiveId } : d);
    wizardSaveInFlight = run;
    setSaving(true);
    try {
      const savedId = await run;
      if (savedId) {
        if (!wizardCreatedId) wizardCreatedId = savedId;
        if (!d._id) {
          setDraft({ ...d, _id: savedId });
          setPersisted(true);
        }
        setLastSavedAt(Date.now());
      }
      setSaveError(null);
      return savedId;
    } catch (err) {
      // Surface the failure — silently pretending the draft is saved cost the
      // testers their campaign settings in the last iteration.
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setSaveError(msg);
      toast.error(`Saving campaign failed: ${msg}`);
      return null;
    } finally {
      if (wizardSaveInFlight === run) wizardSaveInFlight = null;
      setSaving(false);
    }
  };

  /**
   * Called before leaving the wizard.
   * - Persisted drafts: auto-save then allow.
   * - New campaign with data: ask user whether to save as draft.
   * - No data: allow silently.
   * Always returns true (navigation is never blocked).
   */
  /**
   * Starts the message regeneration this editing session queued up. Edits made
   * while editing an already-prepared campaign (Sales Reps, sequence steps,
   * offering) only FLAG the affected messages server-side — nothing is
   * generated while the user is still in the stepper, so the campaign keeps its
   * editable status and one edit can follow another. Leaving the wizard is what
   * triggers the single generation pass.
   */
  const flushPendingRegeneration = async (idToFlush: string): Promise<void> => {
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(idToFlush)}/messages/regenerate-pending`, {
        method: 'POST',
      });
      const data = await resp.json().catch(() => null);
      // The queue is being drained now — clear the "changes pending" indicator so
      // it never lingers, and fire the ONE bundled toast that replaces the
      // per-edit ones.
      setRegenPending(false);
      if (data?.ok && data.queued > 0) {
        toast.success(`Applying your changes — regenerating ${data.queued} message${data.queued === 1 ? '' : 's'}…`);
      }
    } catch {
      // Non-fatal: the queue is persisted server-side, so it can be drained later.
    }
  };

  /**
   * One in-place "changes will regenerate on exit" signal instead of a toast per
   * edit. Editing an already-prepared campaign defers regeneration to exit (see
   * flushPendingRegeneration); a toast on every step / offering / rep edit used
   * to stack into a wall of near-identical messages (UAT 4 Sep). This flips a
   * single quiet indicator in the header instead, and the one bundled toast
   * still fires on leave.
   */
  const noteDeferredRegen = (queued: number): void => {
    if (queued > 0) setRegenPending(true);
  };

  const attemptLeave = (): Promise<boolean> => {
    // Reuse in-flight promise so concurrent callers share one modal
    if (wizardLeaveInFlight) return wizardLeaveInFlight;
    wizardLeaveInFlight = (async () => {
      try {
        // Campaign was already finished — nothing to save
        if (finished) return true;
        // Already persisted (incl. just auto-created) — save silently.
        if (persisted || draft._id || wizardCreatedId) {
          await persistDraft(draft);
          const editedId = draft._id ?? wizardCreatedId;
          if (isEditingNonDraft && editedId) {
            // A valid Sales-Rep change made in the last debounce window would
            // otherwise be cancelled by the effect's cleanup on unmount and
            // silently lost — exactly the "you think it saved but it didn't"
            // trap the auto-apply removes. Commit it here first, synchronously.
            if (pendingRepsTimer.current) {
              clearTimeout(pendingRepsTimer.current);
              pendingRepsTimer.current = null;
            }
            if (salesRepAutoApplyReady()) await applySalesRepChanges({ auto: true });
            await flushPendingRegeneration(editedId);
          }
          return true;
        }
        if (!hasMeaningfulData()) {
          return true;
        }
        const wantsSave = await confirmDialog('Do you want to save this campaign as a draft?', {
          title: 'Save Draft?',
          confirmLabel: 'Save',
          confirmClass: 'btn-primary',
        });
        if (wantsSave) {
          await persistDraft(draft);
        }
        return true;
      } finally {
        wizardLeaveInFlight = null;
      }
    })();
    return wizardLeaveInFlight;
  };

  // Update the guard ref on every render so it captures latest state
  wizardBeforeLeave = attemptLeave;
  useEffect(() => {
    // Seed the create-guard: editing an existing campaign already has an id, a
    // fresh wizard starts with none. Reset on unmount so a subsequent "New
    // campaign" in the same session can't reuse a stale id.
    wizardCreatedId = campaignId ?? null;
    wizardSaveInFlight = null;
    return () => {
      wizardBeforeLeave = null;
      wizardCreatedId = null;
      wizardSaveInFlight = null;
    };
  }, []);

  // Global routing hook — intercepts ALL navigation (navbar, back button, etc.)
  useEffect(() => {
    const hookId = registerRoutingHook(async () => {
      if (wizardBeforeLeave) {
        await wizardBeforeLeave();
      }
      return true;
    });
    return () => {
      removeRoutingHook(hookId);
    };
  }, []);

  /**
   * Required-field gate for a step: returns a human-readable reason why this
   * step's required fields aren't satisfied yet (so the user can't advance past
   * it), or null when the step is good to leave. One source of truth for the
   * "Next" button, the inline hint, the stepper dots and the goToStep guard.
   */
  const stepBlockReason = (key: WizardStepKey | undefined): string | null => {
    if (key === 'leads') {
      if (!draft.name.trim()) {
        return 'Enter a campaign name to continue.';
      }
    }
    if (key === 'offering') {
      if (offeringBusy) {
        return 'Please wait until the document/website finishes processing.';
      }
      if (!draft.offering.name.trim()) {
        return 'Enter a product or service name to continue.';
      }
    }
    if (key === 'type') {
      if (draft.aiEmployeeAssignments.length === 0) {
        return 'Select at least one Sales Representative to continue.';
      }
      const totalPct = draft.aiEmployeeAssignments.reduce((s, a) => s + (a.percentage || 0), 0);
      if (totalPct !== 100) {
        return 'Sales Representative shares must total 100% before continuing.';
      }
      // Hard-gate only a rep that can send on NO channel the sequence uses (no
      // account at all). A rep missing just ONE channel is a coverage gap the
      // Summary step resolves — borrow another rep's account or acknowledge the
      // skip — so it is let through here. Blocking those trapped the user before
      // they could ever reach that resolution.
      if (salesRepsWithoutAnyUsableAccount(liveDraft.current).length > 0) {
        return SALES_REP_ACCOUNT_BLOCK;
      }
      // The floor, for a draft whose sequence has no steps yet: no channel is
      // "required" so the check above can't fire, but nothing can be sent from
      // a campaign with no account at all. Stays a hard gate with the banner —
      // there is no inline hint in this case to soft-gate behind.
      if (draft.emailAccounts.length === 0 && draft.linkedinAccountIds.length === 0) {
        return 'Connect and select at least one email or LinkedIn account for your Sales Representative before continuing.';
      }
    }
    if (key === 'sequence') {
      if (draft.sequence.length === 0) {
        return 'Add at least one sequence step to continue.';
      }
      // Same rule as the Sales Rep step: only a rep that can serve NO required
      // channel blocks here. A coverage gap (one channel missing) is resolved on
      // the Summary step's Channel Coverage Warning, not blocked before it.
      if (salesRepsWithoutAnyUsableAccount(liveDraft.current).length > 0) {
        return SALES_REP_ACCOUNT_BLOCK;
      }
      const overLongConnect = draft.sequence.some(
        s => s.channel === 'linkedin_connect' && (s.manualBody?.length ?? 0) > LINKEDIN_CONNECT_NOTE_MAX_CHARS,
      );
      if (overLongConnect) {
        return `Shorten the connection request message to ${LINKEDIN_CONNECT_NOTE_MAX_CHARS} characters or fewer — LinkedIn rejects longer invitations.`;
      }
    }
    return null;
  };

  const goToStep = async (nextStep: number) => {
    // Block ALL navigation while the Offering step is still parsing/analyzing —
    // leaving now would throw away the in-progress result.
    if (offeringBusy && STEPS[currentStep]?.key === 'offering') return;
    // Gate forward navigation: every step we'd move past must have its required
    // fields filled. Covers the Next button AND direct stepper-dot jumps.
    if (nextStep > currentStep) {
      for (let i = currentStep; i < nextStep; i++) {
        const reason = stepBlockReason(STEPS[i]?.key);
        if (reason) {
          // Only a soft-gated step ever gets here — every other gate disables
          // "Next" outright, so there is no click to answer. This is the error
          // the Sales Rep step's quiet inline hint escalates to once the user
          // actually tries to move on without picking an account. That attempt
          // is also what first reveals those per-rep hints (they are held back
          // until now, so a rep just ticked is not marked before they can act).
          if (reason === SALES_REP_ACCOUNT_BLOCK) setRevealRepGaps(true);
          toast.error(reason);
          return;
        }
      }
    }
    if (nextStep > currentStep && STEPS[currentStep]?.key === 'leads' && draft.apolloSearchInput) {
      await persistDraft(draft);
    }
    setCurrentStep(nextStep);
    setMaxStepReached(Math.max(maxStepReached, nextStep));
  };

  // Load existing campaign if editing
  useEffect(() => {
    if (!campaignId || loaded) return;
    (async () => {
      try {
        const resp = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}`);
        const data = await resp.json();
        if (data.ok && data.campaign) {
          const c = data.campaign;
          // Load leads before building draft to avoid stale-closure issues
          let leads: any[] = [];
          try {
            const leadsResp = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/leads`);
            const leadsData = await leadsResp.json();
            if (leadsData.ok && leadsData.leads) {
              leads = leadsData.leads;
            }
          } catch {
            /* ignore lead load errors */
          }
          const loadedSequence = (c.sequence ?? []).map((s: any) => ({
            id: s.stepId ?? s.id,
            channel: s.channel,
            delayValue: s.delayValue,
            delayUnit: s.delayUnit,
            condition: s.condition,
            prompt: s.prompt,
            subject: s.subject,
            subjectPrompt: s.subjectPrompt ?? '',
            manualBody: s.manualBody ?? undefined,
            previewSubject: s.previewSubject ?? undefined,
            previewBody: s.previewBody ?? undefined,
            previewKey: s.previewKey ?? undefined,
            refineInstruction: s.refineInstruction ?? undefined,
          }));
          // Every step already loaded from the server already has messages
          // generated for it (or is legitimately still 'draft', where the
          // edit-mode restrictions never apply anyway) — snapshot their ids so
          // handleSequenceChange can tell them apart from steps added THIS
          // session, which aren't confirmed/generated until their debounce fires.
          confirmedStepIds.current = new Set(loadedSequence.map((s: { id: string }) => s.id));
          // Hoisted out of setDraft so it can double as the applied-assignments
          // snapshot (the server-side truth the Apply bar diffs against). The
          // `?? a.doc?.Y` fallbacks recover corrupted docs — see the comment on
          // llmModel below.
          const loadedAssignments: CampaignDraft['aiEmployeeAssignments'] =
            Array.isArray(c.aiEmployeeAssignments) && c.aiEmployeeAssignments.length > 0
              ? c.aiEmployeeAssignments.map((a: any) => ({
                  aiEmployeeId: a.aiEmployeeId ?? a.doc?.aiEmployeeId,
                  percentage: Number(a.percentage ?? a.doc?.percentage) || 0,
                }))
              : c.aiEmployeeId
                ? [{ aiEmployeeId: c.aiEmployeeId, percentage: 100 }]
                : [];
          appliedAssignments.current = loadedAssignments.map(a => ({ ...a }));
          // Hoisted for the same reason as loadedAssignments — these double as
          // the server-side truth the Apply bar diffs the account picks against.
          const loadedEmailByAie: Record<string, string[]> = Array.isArray(c.emailAccountsByAie)
            ? Object.fromEntries(
                c.emailAccountsByAie.map((b: any) => [
                  b.aiEmployeeId ?? b.doc?.aiEmployeeId,
                  b.emailAccounts ?? b.doc?.emailAccounts ?? [],
                ]),
              )
            : c.aiEmployeeId && (c.emailAccounts ?? []).length > 0
              ? { [c.aiEmployeeId]: c.emailAccounts }
              : {};
          const loadedLinkedinByAie: Record<string, string[]> = Array.isArray(c.linkedinAccountIdsByAie)
            ? Object.fromEntries(
                c.linkedinAccountIdsByAie.map((b: any) => [
                  b.aiEmployeeId ?? b.doc?.aiEmployeeId,
                  b.linkedinAccountIds ?? b.doc?.linkedinAccountIds ?? [],
                ]),
              )
            : c.aiEmployeeId && (c.linkedinAccountIds ?? []).length > 0
              ? { [c.aiEmployeeId]: c.linkedinAccountIds }
              : {};
          appliedEmailAccountsByAie.current = structuredClone(loadedEmailByAie);
          appliedLinkedinAccountIdsByAie.current = structuredClone(loadedLinkedinByAie);
          setDraft({
            _id: String(c._id),
            externalCampaignId: c.externalCampaignId ?? undefined,
            status: c.status,
            launchedAt: c.launchedAt ?? undefined,
            name: c.name ?? '',
            campaignType: c.campaignType || null,
            offering: c.offering ?? { name: '', description: '', talkingPoints: [] },
            leads,
            schedule: c.schedule
              ? {
                  ...c.schedule,
                  startTime: parseTimeValue(c.schedule.startTime),
                  endTime: parseTimeValue(c.schedule.endTime),
                }
              : emptyCampaignDraft().schedule,
            sequence: loadedSequence,
            emailAccounts: c.emailAccounts ?? [],
            linkedinAccountIds: c.linkedinAccountIds ?? (c.linkedinAccountId ? [c.linkedinAccountId] : []),
            // The `?? x.doc?.Y` fallbacks below recover campaigns whose
            // llmModel/aiEmployeeAssignments/emailAccountsByAie/
            // linkedinAccountIdsByAie were corrupted by a since-fixed
            // server-side bug (an entity instance written straight through a
            // raw MongoDB updateOne serializes its non-enumerable internals
            // instead of its real fields, burying them at e.g. `a.doc.
            // aiEmployeeId`) — without this, an older affected campaign shows
            // no Sales Rep selected here even though one was actually chosen.
            llmModel: c.llmModel
              ? {
                  id: c.llmModel.llmId ?? c.llmModel.id ?? c.llmModel.doc?.llmId ?? c.llmModel.doc?.id,
                  model: c.llmModel.model ?? c.llmModel.doc?.model,
                  label: c.llmModel.label ?? c.llmModel.doc?.label,
                }
              : null,
            aiEmployeeId: c.aiEmployeeId ?? null,
            aiEmployeeAssignments: loadedAssignments,
            emailAccountsByAie: loadedEmailByAie,
            linkedinAccountIdsByAie: loadedLinkedinByAie,
            channelFallbacks: Array.isArray(c.channelFallbacks)
              ? c.channelFallbacks.map((f: any) => ({
                  sourceAiEmployeeId: f.sourceAiEmployeeId,
                  channel: f.channel,
                  fallbackAiEmployeeId: f.fallbackAiEmployeeId,
                }))
              : [],
            autoReply: c.autoReply ?? false,
            replyPrompt: c.replyPrompt ?? '',
            crmAutoSync: c.crmAutoSync ?? false,
            language: c.language ?? 'en',
            replyLlmModel: c.replyLlmModel
              ? {
                  id: c.replyLlmModel.llmId ?? c.replyLlmModel.id,
                  model: c.replyLlmModel.model,
                  label: c.replyLlmModel.label,
                }
              : null,
            apolloSearchInput: c.apolloSearchInputJson ? JSON.parse(c.apolloSearchInputJson) : undefined,
            projectId: c.projectId ?? undefined,
          });
          setPersisted(true);
        }
      } catch {
        // Ignore load errors, start fresh
      }
      setLoaded(true);
    })();
  }, [campaignId, loaded]);

  // Auto-save the draft after edits. Unlike before, this also covers BRAND-NEW
  // drafts: the first save creates the campaign (once it has real, save-worthy
  // data), every later edit updates it. `persistDraft` serializes saves so a
  // burst of edits can never create duplicate campaigns.
  useEffect(() => {
    // Don't fire while an existing campaign is still loading into the draft.
    if (campaignId && !loaded) return;
    if (!hasSaveworthyData()) return;
    const timeout = setTimeout(() => {
      void persistDraft(draft);
    }, 1500);
    return () => clearTimeout(timeout);
  }, [
    draft.name,
    draft.campaignType,
    draft.offering,
    draft.schedule,
    draft.sequence,
    draft.emailAccounts,
    draft.linkedinAccountIds,
    draft.llmModel,
    draft.aiEmployeeId,
    draft.aiEmployeeAssignments,
    draft.emailAccountsByAie,
    draft.linkedinAccountIdsByAie,
    draft.leads,
    draft.autoReply,
    draft.replyPrompt,
    draft.replyLlmModel,
    draft.crmAutoSync,
    draft.language,
    draft.apolloSearchInput,
    draft.projectId,
    persisted,
  ]);

  /**
   * Put the placeholder step on a channel this user can actually send on.
   *
   * Every new draft opens with one EMAIL step (see emptyCampaignDraft), and the
   * Sales Rep step — which comes BEFORE the sequence builder — derives the
   * channels a rep needs an account for from exactly that sequence. So someone
   * who only ever connected LinkedIn was asked for a mailbox they don't have,
   * for a step they never added, before they had even seen the sequence. The
   * placeholder is our guess, not their decision, so we correct it as soon as we
   * know what they have: no mailbox but a LinkedIn seat means the campaign they
   * are about to build starts on LinkedIn.
   *
   * Only ever replaces the untouched placeholder, and only on a brand-new draft.
   */
  useEffect(() => {
    if (campaignId) return;
    void (async () => {
      const linkedinRes = await fetch('/api/accounts/linkedin')
        .then(r => r.json())
        .catch(() => ({ ok: false }));
      // Nothing to re-seed to — leave the email placeholder alone. Checked first
      // so the common email-only user never pays for the second request.
      if (!linkedinRes.ok || (linkedinRes.accounts ?? []).length === 0) return;
      // Email accounts come from two sources now — Instantly (retiring) and
      // Unipile (the new path). Only re-seed to LinkedIn when we can confirm the
      // user has NEITHER; a failed lookup means we cannot be sure, so leave the
      // placeholder alone rather than push an email user onto LinkedIn.
      const [emailRes, unipileEmailRes] = await Promise.all([
        fetch('/api/accounts/email')
          .then(r => r.json())
          .catch(() => ({ ok: false })),
        fetch('/api/accounts/unipile-email')
          .then(r => r.json())
          .catch(() => ({ ok: false })),
      ]);
      if (!emailRes.ok || !unipileEmailRes.ok) return;
      if ((emailRes.accounts ?? []).length > 0 || (unipileEmailRes.accounts ?? []).length > 0) return;
      // The user may have shaped the sequence while those requests were in
      // flight — re-check against the live draft, never the mount-time one.
      if (!isPlaceholderSequence(liveDraft.current.sequence)) return;
      const sequence = defaultLinkedinSequence().slice(0, 1);
      setDraft({ ...liveDraft.current, sequence, campaignType: deriveCampaignType(sequence) });
    })();
  }, []);

  // Keep maxStepReached in sync whenever user navigates
  useEffect(() => {
    if (currentStep > maxStepReached) setMaxStepReached(currentStep);
    // The busy flag is only meaningful on the Offering step — clear it whenever
    // we're somewhere else so a stale value can't permanently block navigation.
    if (STEPS[currentStep]?.key !== 'offering') setOfferingBusy(false);
    // Smoothly scroll the main scroll container back to top when the step changes
    requestAnimationFrame(() => {
      const main = document.getElementById('main-content');
      (main ?? window).scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, [currentStep]);

  const canGoBack = currentStep > 0;
  const canGoForward = currentStep < STEPS.length - 1;

  /**
   * Reason the user cannot advance from the *current* step yet, or null when
   * forward navigation is allowed. Thin wrapper over `stepBlockReason` so the
   * disabled "Next" button and the inline explanation share the gate logic.
   */
  const forwardBlockReason = (): string | null => stepBlockReason(STEPS[currentStep]?.key);

  /** Applies an assignments change to the draft (prunes per-AIE account buckets for reps no longer assigned). */
  const applyAssignmentsChange = (
    aiEmployeeAssignments: CampaignDraft['aiEmployeeAssignments'],
    // Accounts the Sales Rep step wants pre-selected for a rep that was just
    // checked (their single account for a channel this campaign actually
    // uses). It has to ride along here rather than come through a second
    // setter for the reason spelled out below: two setters in one event handler
    // means the later one spreads a stale draft.
    seed?: { emailAccountsByAie?: Record<string, string[]>; linkedinAccountIdsByAie?: Record<string, string[]> },
  ) => {
    // A rep-set change starts the missing-account hints quiet again: the user
    // has just ticked/unticked someone and has not yet had a chance to pick an
    // account for them, so nagging now is exactly what this ticket removed. The
    // hints come back the moment they try to advance (goToStep).
    setRevealRepGaps(false);
    // Prune per-Sales-Rep account buckets for any rep that's no longer
    // assigned, all in a single setDraft so we don't drop changes via
    // stale-closure overwrites in subsequent setters.
    const validIds = new Set(aiEmployeeAssignments.map(a => a.aiEmployeeId));
    const emailAccountsByAie = Object.fromEntries(
      Object.entries({ ...liveDraft.current.emailAccountsByAie, ...(seed?.emailAccountsByAie ?? {}) }).filter(([id]) =>
        validIds.has(id),
      ),
    );
    const linkedinAccountIdsByAie = Object.fromEntries(
      Object.entries({ ...liveDraft.current.linkedinAccountIdsByAie, ...(seed?.linkedinAccountIdsByAie ?? {}) }).filter(
        ([id]) => validIds.has(id),
      ),
    );
    setDraft({
      ...liveDraft.current,
      aiEmployeeAssignments,
      // Mirror legacy single-AIE field for backward compat in API + checks.
      aiEmployeeId: aiEmployeeAssignments[0]?.aiEmployeeId ?? null,
      emailAccountsByAie,
      linkedinAccountIdsByAie,
      emailAccounts: Array.from(new Set(Object.values(emailAccountsByAie).flat())),
      linkedinAccountIds: Array.from(new Set(Object.values(linkedinAccountIdsByAie).flat())),
    });
  };

  /** How many times a step submit waits out a "still generating" refusal before giving up. */
  const MAX_STEP_SUBMIT_ATTEMPTS = 6;
  /** …and how long it waits between those tries. Six of these covers a slow first batch. */
  const STEP_SUBMIT_RETRY_MS = 2500;

  /**
   * Actually creates a not-yet-confirmed step server-side once its debounce
   * fires. `attempt` counts the tries spent waiting out a temporary refusal —
   * see the retry below.
   */
  const submitNewStep = async (stepId: string, attempt = 1) => {
    pendingStepTimers.delete(stepId);
    const step = liveDraft.current.sequence.find(s => s.id === stepId);
    // Gone already (user removed it before the debounce fired) or still empty
    // (nothing worth saving yet — the next edit will re-arm the debounce).
    if (!step || !step.prompt?.trim()) return;
    // Undo the optimistic local insert when the server rejects the step —
    // otherwise a ghost step stays visible (and the autosave PUT would even
    // persist it into campaign.sequence) although no messages exist for it.
    const revertStep = () => {
      const nextSequence = liveDraft.current.sequence.filter(s => s.id !== stepId);
      setDraft({ ...liveDraft.current, sequence: nextSequence, campaignType: deriveCampaignType(nextSequence) });
    };
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(draft._id!)}/sequence/steps`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // Our id, not the server's. The autosave PUT below persists
          // campaign.sequence carrying THIS id, so if the server minted its own
          // the step would answer to two — messages under one, the sequence
          // under the other — and every stepId lookup afterwards would miss.
          stepId: step.id,
          channel: step.channel,
          delayValue: step.delayValue,
          delayUnit: step.delayUnit,
          condition: step.condition,
          prompt: step.prompt,
          subject: step.subject,
          subjectPrompt: step.subjectPrompt,
          manualBody: step.manualBody,
        }),
      });
      const data = await resp.json();
      if (!data.ok) {
        // QA #2: the first batch is still generating. That refusal passes by
        // itself within a moment, and throwing the user's typed step away for it
        // meant the step vanished and only a second attempt stuck. Wait for it
        // instead — bounded, so a refusal that is NOT temporary still surfaces.
        if (data.retryable && attempt < MAX_STEP_SUBMIT_ATTEMPTS) {
          if (attempt === 1) toast.info(data.error ?? 'The first batch is still generating — saving your step shortly…');
          pendingStepTimers.set(
            stepId,
            setTimeout(() => void submitNewStep(stepId, attempt + 1), STEP_SUBMIT_RETRY_MS),
          );
          return;
        }
        toast.error(data.error ?? 'Failed to add step');
        revertStep();
        return;
      }
      confirmedStepIds.current.add(stepId);
      if ((data.ineligibleLeads ?? 0) > 0) {
        // The new step's channel needs contact data some existing leads lack
        // (e.g. an email step added to a formerly LinkedIn-only campaign) —
        // they skip this step. Point the user at the Apollo top-up, which
        // sources leads carrying the full channel mix's contact data.
        //
        // NAME THE PAUSE. This sentence used to say "Source new leads via Add
        // Leads" full stop, and on an ACTIVE campaign the top-up route answers
        // 409: it accepts `ready_to_launch`, `preparing_to_launch` and `paused`
        // only. So the product pointed at a door it had locked, and the user
        // found out by being refused.
        //
        // Pause → top up → resume IS the supported flow, and the route says so
        // itself: resuming goes back through the launch cool-down, which gives
        // the new batch a full hour to finish generating before sending picks
        // up again. It was never missing — only unsaid.
        toast.warning(
          `Step added — ${data.ineligibleLeads} of ${data.totalLeads} leads ${
            data.ineligibleLeads === 1 ? 'is' : 'are'
          } missing ${data.missingContactData} and will skip this step. To reach the full count: pause the ` +
            `campaign, then source new leads via "Add Leads" in the Leads step, then resume.`,
          10000,
        );
      } else {
        toast.success(
          `Step added — generating ${data.generableMessages} message${data.generableMessages === 1 ? '' : 's'}…`,
        );
      }
    } catch {
      toast.error('Failed to add step (network error)');
      revertStep();
    }
  };

  /**
   * Actually persists a remove/reorder of already-confirmed steps. Doesn't
   * touch campaign.sequence (the regular autosave PUT already does that,
   * stepId-safe) — only cleans up CampaignMessage docs for removed steps and
   * re-stamps stepIndex on the rest so it matches their new position.
   * Reverts the optimistic local change on failure so the UI never shows a
   * remove/reorder as applied when the server rejected it.
   */
  const submitSequenceReorder = async (
    removedStepIds: string[],
    orderedStepIds: string[],
    previousSequence: CampaignDraft['sequence'],
  ) => {
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(draft._id!)}/sequence/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderedStepIds, removedStepIds }),
      });
      const data = await resp.json();
      if (!data.ok) {
        toast.error(data.error ?? 'Failed to update the sequence');
        for (const id of removedStepIds) confirmedStepIds.current.add(id); // undo the optimistic removal
        setDraft({
          ...liveDraft.current,
          sequence: previousSequence,
          campaignType: deriveCampaignType(previousSequence),
        });
        return;
      }
      if (removedStepIds.length > 0) {
        toast.success(
          `Step removed — ${data.removedMessages} message${data.removedMessages === 1 ? '' : 's'} deleted.`,
        );
      }
    } catch {
      toast.error('Failed to update the sequence (network error)');
      for (const id of removedStepIds) confirmedStepIds.current.add(id);
      setDraft({
        ...liveDraft.current,
        sequence: previousSequence,
        campaignType: deriveCampaignType(previousSequence),
      });
    }
  };

  /** Content fields that, if changed on an already-confirmed step, require regenerating its messages. Deliberately excludes `channel` — the UI never offers a way to change an existing step's channel (see wiz-sequence.ts), and the server-side endpoint ignores it too. */
  const STEP_CONTENT_FIELDS = [
    'delayValue',
    'delayUnit',
    'condition',
    'prompt',
    'subject',
    'subjectPrompt',
    'manualBody',
  ] as const;
  const stepContentChanged = (a: SequenceStep, b: SequenceStep): boolean =>
    STEP_CONTENT_FIELDS.some(f => a[f] !== b[f]);

  /** Actually persists a content edit to an already-confirmed step once its debounce fires. */
  const submitStepContentChange = async (stepId: string) => {
    pendingStepTimers.delete(stepId);
    const step = liveDraft.current.sequence.find(s => s.id === stepId);
    // Gone already, or reverted back to not-confirmed (shouldn't happen, but
    // guard anyway), or emptied out — nothing worth submitting.
    if (!step || !confirmedStepIds.current.has(stepId) || (!step.prompt?.trim() && !step.manualBody?.trim())) return;
    try {
      const resp = await fetch(
        `/api/campaigns/${encodeURIComponent(draft._id!)}/sequence/steps/${encodeURIComponent(stepId)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            delayValue: step.delayValue,
            delayUnit: step.delayUnit,
            condition: step.condition,
            prompt: step.prompt,
            subject: step.subject,
            subjectPrompt: step.subjectPrompt,
            manualBody: step.manualBody,
          }),
        },
      );
      const data = await resp.json();
      if (!data.ok) {
        toast.error(data.error ?? 'Failed to update step');
        return;
      }
      // No per-edit toast — it stacked. One quiet indicator instead; the single
      // bundled toast fires on exit (flushPendingRegeneration).
      noteDeferredRegen(data.messagesQueued);
    } catch {
      toast.error('Failed to update step (network error)');
    }
  };

  /**
   * Sequence-step changes when editing an already-prepared campaign. Every
   * mutation in wiz-sequence.ts (add/remove/reorder/edit-content) funnels
   * through this single callback. `confirmedStepIds` (snapshotted at load,
   * grown as new steps get confirmed below) tells apart:
   *  - an id already in `confirmedStepIds`: already has generated messages.
   *    Content edits (prompt/subject/manualBody/timing/condition) are applied
   *    locally immediately and debounced before PUTting to the dedicated
   *    per-step endpoint, which regenerates that step's messages — the plain
   *    autosave PUT still fires too (harmless field mirroring, no regen).
   *    Removing a confirmed step, or changing the relative order of confirmed
   *    steps, is applied optimistically and synced via the dedicated reorder
   *    endpoint (cleans up CampaignMessage docs / re-stamps stepIndex — see
   *    submitSequenceReorder above); a failure reverts the local change.
   *  - an id NOT in `confirmedStepIds`: a step added THIS session, still being
   *    typed into. Applied locally immediately (so typing feels normal) and
   *    debounced (mirrors the 1.5s autosave) before POSTing it to the
   *    dedicated add-step endpoint — never persisted with an empty prompt.
   *    Removing it before the debounce fires is always fine (nothing was ever
   *    saved server-side for it).
   */
  const handleSequenceChange = (newSequence: CampaignDraft['sequence']) => {
    if (!isEditingNonDraft || !draft._id) {
      setDraft({ ...liveDraft.current, sequence: newSequence, campaignType: deriveCampaignType(newSequence) });
      return;
    }
    const previousSequence = liveDraft.current.sequence;
    const previousById = new Map(previousSequence.map(s => [s.id, s]));
    const newIds = new Set(newSequence.map(s => s.id));
    const removedConfirmed = [...confirmedStepIds.current].filter(id => !newIds.has(id));
    // Reordering an already-confirmed step would leave its persisted
    // CampaignMessage.stepIndex stale (set once at creation, never
    // recalculated) — compare the relative order of SURVIVING confirmed ids
    // only, so inserting a new step between two confirmed ones, or removing
    // one, isn't also flagged as a reorder of the ones that didn't move.
    const survivingPrevOrder = previousSequence
      .filter(s => confirmedStepIds.current.has(s.id) && newIds.has(s.id))
      .map(s => s.id);
    const newConfirmedOrder = newSequence.filter(s => confirmedStepIds.current.has(s.id)).map(s => s.id);
    const orderChanged = survivingPrevOrder.some((id, i) => id !== newConfirmedOrder[i]);

    // A pending (not-yet-confirmed) step the user just removed — cancel its
    // debounce so it's never submitted.
    for (const [id, timer] of pendingStepTimers) {
      if (!newIds.has(id)) {
        clearTimeout(timer);
        pendingStepTimers.delete(id);
      }
    }
    for (const id of removedConfirmed) confirmedStepIds.current.delete(id);
    setDraft({ ...liveDraft.current, sequence: newSequence, campaignType: deriveCampaignType(newSequence) });
    for (const step of newSequence) {
      if (!confirmedStepIds.current.has(step.id)) {
        // Not-yet-confirmed step — debounce the create-step call.
        const existingTimer = pendingStepTimers.get(step.id);
        if (existingTimer) clearTimeout(existingTimer);
        pendingStepTimers.set(
          step.id,
          setTimeout(() => void submitNewStep(step.id), 1500),
        );
        continue;
      }
      // Already-confirmed step — debounce a content-change call only if its
      // content actually differs from what's persisted (avoids firing on
      // every render / on reorders that don't touch this step's own fields).
      const prev = previousById.get(step.id);
      if (!prev || !stepContentChanged(prev, step)) continue;
      const existingTimer = pendingStepTimers.get(step.id);
      if (existingTimer) clearTimeout(existingTimer);
      pendingStepTimers.set(
        step.id,
        setTimeout(() => void submitStepContentChange(step.id), 1500),
      );
    }
    if (removedConfirmed.length > 0 || orderChanged) {
      void submitSequenceReorder(removedConfirmed, newConfirmedOrder, previousSequence);
    }
  };

  /**
   * Offering changes when editing an already-prepared campaign. The plain
   * autosave PUT would silently update campaign.offering without touching any
   * already-generated message — the dedicated /offering endpoint regenerates
   * every non-manual, non-deferred message against the new product
   * description/talking points instead.
   */
  const submitOfferingChange = async () => {
    pendingOfferingTimer.current = null;
    const offering = liveDraft.current.offering;
    if (!draft._id || !offering.name?.trim()) return;
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(draft._id)}/offering`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: offering.name,
          description: offering.description,
          talkingPoints: offering.talkingPoints,
        }),
      });
      const data = await resp.json();
      if (!data.ok) {
        toast.error(data.error ?? 'Failed to update offering');
        return;
      }
      // No per-edit toast — one quiet indicator; the bundled toast fires on exit.
      noteDeferredRegen(data.messagesQueued);
    } catch {
      toast.error('Failed to update offering (network error)');
    }
  };
  const handleOfferingChange = (offering: CampaignDraft['offering']) => {
    setDraft({ ...liveDraft.current, offering });
    if (!isEditingNonDraft || !draft._id) return;
    if (pendingOfferingTimer.current) clearTimeout(pendingOfferingTimer.current);
    pendingOfferingTimer.current = setTimeout(() => void submitOfferingChange(), 1500);
  };

  /**
   * Channels this campaign actually sends on. A Sales Rep only needs an
   * account for a channel the sequence uses — an email-only campaign doesn't
   * care that a rep has no LinkedIn seat, and the untouched placeholder step
   * demands nothing (see channelsRequiringAccount).
   */
  const requiredChannels = (): { email: boolean; linkedin: boolean } =>
    channelsRequiringAccount(liveDraft.current.sequence);

  /**
   * Which required channels are missing a sending account on at least one
   * assigned Sales Rep. This is exactly what the /sales-reps endpoint rejects,
   * evaluated up-front so the Sales Rep step can point at the empty account
   * picker instead of letting the user hit Apply and get a red error back.
   * A rep with a configured channel fallback borrows someone else's accounts
   * server-side, so an empty bucket is fine for them.
   */
  const salesRepAccountGaps = (): { email: boolean; linkedin: boolean } => {
    const need = requiredChannels();
    const d = liveDraft.current;
    const hasFallback = (aiEmployeeId: string, channel: 'email' | 'linkedin') =>
      (d.channelFallbacks ?? []).some(f => f.sourceAiEmployeeId === aiEmployeeId && f.channel === channel);
    return {
      email:
        need.email &&
        d.aiEmployeeAssignments.some(
          a => (d.emailAccountsByAie[a.aiEmployeeId] ?? []).length === 0 && !hasFallback(a.aiEmployeeId, 'email'),
        ),
      linkedin:
        need.linkedin &&
        d.aiEmployeeAssignments.some(
          a =>
            (d.linkedinAccountIdsByAie[a.aiEmployeeId] ?? []).length === 0 && !hasFallback(a.aiEmployeeId, 'linkedin'),
        ),
    };
  };

  /**
   * What the Apply gate is still waiting for, in two registers: `hint` is the
   * quiet line in the pending-changes bar, shown as soon as something is
   * missing; `error` is what the user gets if they hit Apply anyway. Same
   * condition, different volume — the bar has no business shouting at someone
   * who is three seconds away from picking the account themselves. Null once
   * there's nothing left to wait for.
   */
  const salesRepApplyGate = (): { hint: string; error: string } | null => {
    const both = (message: string) => ({ hint: message, error: message });
    const assignments = liveDraft.current.aiEmployeeAssignments;
    if (assignments.length === 0) return both('Select at least one Sales Representative.');
    if (assignments.reduce((s, a) => s + (a.percentage || 0), 0) !== 100)
      return both('Sales Representative shares must total 100%.');
    const gaps = salesRepAccountGaps();
    if (!gaps.email && !gaps.linkedin) return null;
    const what =
      gaps.email && gaps.linkedin
        ? 'an email and a LinkedIn account'
        : gaps.email
          ? 'an email account'
          : 'a LinkedIn account';
    return { hint: `Please select ${what} for the highlighted Sales Rep below.`, error: SALES_REP_ACCOUNT_BLOCK };
  };

  /**
   * Commits the draft's Sales Rep assignments AND their per-rep sending accounts
   * to the server when editing an already-prepared campaign. The dedicated
   * /sales-reps endpoint re-keys leads to the new split and regenerates the moved
   * leads' messages in their new persona's voice, which a plain autosave would
   * silently skip (saveDraftToApi deliberately omits the whole Sales Rep block
   * for non-draft campaigns for exactly that reason).
   *
   * Called automatically (debounced) once the picker settles into a valid,
   * applicable state — there is no "Apply" button any more (UAT 4 Sep). `auto`
   * keeps that self-commit quiet: it never toasts a validation gate or a no-op,
   * because the picker's inline hint already covers those, and a bare
   * percentage tweak or a blocked removal simply isn't committed.
   */
  const applySalesRepChanges = async (opts?: { auto?: boolean }) => {
    const auto = opts?.auto ?? false;
    if (!draft._id || applyingReps) return;
    const gate = salesRepApplyGate();
    if (gate) {
      if (!auto) toast.error(gate.error);
      return;
    }
    const assignments = liveDraft.current.aiEmployeeAssignments;
    // Snapshot what we actually send, so the applied-state refs below record
    // that and not whatever the user changed while the request was in flight.
    const sentEmailByAie = structuredClone(liveDraft.current.emailAccountsByAie);
    const sentLinkedinByAie = structuredClone(liveDraft.current.linkedinAccountIdsByAie);

    // A launched campaign accepts two kinds of change here:
    //  - a rep-set change (a Sales Rep added, removed, or swapped) falls through
    //    to the full re-split endpoint below. On a launched campaign that
    //    endpoint locks every already-contacted lead to its current rep and
    //    moves only the still-uncontacted ones.
    //  - adding a sender account to a rep already on the campaign takes the
    //    lighter /sender-accounts path, which never re-keys anyone.
    // The rep-set test keys off the SET of rep ids only, never the percentages:
    // the Sales Rep picker re-normalises those on its own, and reading that as a
    // change used to block a perfectly valid account addition (UAT 21 Aug).
    if (draft.launchedAt && !hasRepSetChange(appliedAssignments.current, assignments)) {
      const action = senderAccountApplyAction(
        appliedEmailAccountsByAie.current,
        sentEmailByAie,
        appliedLinkedinAccountIdsByAie.current,
        sentLinkedinByAie,
      );
      if (action === 'blocked-removal') {
        // Not auto-committable — the inline notice in the Sales Rep step explains
        // it. (Auto-apply never reaches here; kept for a defensive direct call.)
        if (!auto)
          toast.error(
            'A sender account can’t be removed from a launched campaign — the leads already sending from it would be ' +
              'stranded. Undo the removal; you can only ADD a sender account here.',
          );
        return;
      }
      if (action === 'nothing-added') {
        if (!auto)
          toast.error(
            'Nothing to apply. On a launched campaign you can add a sender account, or add/remove a Sales Rep — ' +
              'changing only the percentage split isn’t applied here.',
          );
        return;
      }
      setApplyingReps(true);
      try {
        const resp = await fetch(`/api/campaigns/${encodeURIComponent(draft._id)}/sender-accounts`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ emailAccountsByAie: sentEmailByAie, linkedinAccountIdsByAie: sentLinkedinByAie }),
        });
        const data = await resp.json();
        if (!data.ok) {
          toast.error(data.error ?? 'Failed to add the sender account');
          return;
        }
        appliedAssignments.current = assignments.map(a => ({ ...a }));
        appliedEmailAccountsByAie.current = sentEmailByAie;
        appliedLinkedinAccountIdsByAie.current = sentLinkedinByAie;
        // Adding a sender re-balances senders but regenerates nothing, so there
        // is no pending-regen to note — the "All changes saved" indicator is the
        // quiet confirmation. Warnings are real and always shown.
        for (const w of (data.warnings ?? []) as string[]) toast.warning(w);
      } catch {
        toast.error('Failed to add the sender account (network error)');
      } finally {
        setApplyingReps(false);
      }
      return;
    }

    setApplyingReps(true);
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(draft._id)}/sales-reps`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assignments,
          emailAccountsByAie: sentEmailByAie,
          linkedinAccountIdsByAie: sentLinkedinByAie,
        }),
      });
      const data = await resp.json();
      if (!data.ok) {
        // The endpoint refuses while an EARLIER edit's messages are still
        // regenerating (campaign back in 'generating'). That's a transient race,
        // not something the user did wrong.
        const stillGenerating = resp.status === 409 && /finished generating/.test(String(data.error ?? ''));
        if (stillGenerating) {
          // Retry the self-commit shortly, quietly — there is no button to press
          // again now that this applies itself.
          if (pendingRepsTimer.current) clearTimeout(pendingRepsTimer.current);
          pendingRepsTimer.current = setTimeout(() => void applySalesRepChanges({ auto: true }), 3000);
        } else {
          // A real failure must be visible even for the silent self-commit, or a
          // rep change the user believes is saved would be lost without a trace.
          toast.error(data.error ?? 'Failed to change Sales Reps');
        }
        return;
      }
      appliedAssignments.current = assignments.map(a => ({ ...a }));
      appliedEmailAccountsByAie.current = sentEmailByAie;
      appliedLinkedinAccountIdsByAie.current = sentLinkedinByAie;
      // One in-place indicator instead of a per-apply toast; the bundled toast
      // fires on exit.
      noteDeferredRegen(data.messagesQueued);
      // Leads whose outreach had already started keep sending from the LinkedIn
      // account that contacted them, because that is the only account connected
      // to them — so for these the split shown above is not the split that will
      // send. Said here rather than left in a server log: it is the one part of
      // a Sales Rep change that cannot be fixed by changing the numbers.
      const pinned = Number(data.leadsPinnedToPreviousAccount ?? 0);
      if (pinned > 0) {
        toast.warning(
          `${pinned} lead${pinned === 1 ? '' : 's'} had already been contacted and will keep sending from the ` +
            `previous Sales Rep's LinkedIn account — only that account is connected to them.`,
        );
      }
    } catch {
      toast.error('Failed to change Sales Reps (network error)');
    } finally {
      setApplyingReps(false);
    }
  };

  /**
   * Whether a valid, applicable Sales-Rep / sender-account change is sitting in
   * the draft waiting to be committed. Gathers the wizard's live state and
   * defers the decision to the pure `shouldAutoApplySalesReps` (unit-tested in
   * sender-account-plan). Drives both the debounced auto-commit below and the
   * commit-before-leave in attemptLeave.
   */
  const salesRepAutoApplyReady = (): boolean =>
    !!draft._id &&
    shouldAutoApplySalesReps({
      isEditingNonDraft,
      launched: !!draft.launchedAt,
      valid: salesRepApplyGate() === null,
      appliedAssignments: appliedAssignments.current,
      nextAssignments: liveDraft.current.aiEmployeeAssignments,
      appliedEmail: appliedEmailAccountsByAie.current,
      nextEmail: liveDraft.current.emailAccountsByAie,
      appliedLinkedin: appliedLinkedinAccountIdsByAie.current,
      nextLinkedin: liveDraft.current.linkedinAccountIdsByAie,
    });

  // Auto-apply Sales-Rep / sender-account changes in edit mode, so a rep change
  // commits itself like every other field instead of waiting on an "Apply"
  // button that could be forgotten (LinkedIn-UAT 4 Sep). Mirrors the 1.5s
  // autosave debounce and fires ONLY when salesRepAutoApplyReady() — a complete,
  // valid, applicable change. The dedicated /sales-reps endpoint stays the
  // single atomic writer; an incomplete picker keeps its quiet inline hint and
  // commits nothing. A change made in the last debounce window is still
  // committed on exit (see attemptLeave), so nothing is silently lost.
  useEffect(() => {
    if (campaignId && !loaded) return;
    if (applyingReps) return;
    if (!salesRepAutoApplyReady()) return;
    if (pendingRepsTimer.current) clearTimeout(pendingRepsTimer.current);
    pendingRepsTimer.current = setTimeout(() => void applySalesRepChanges({ auto: true }), 1500);
    return () => {
      if (pendingRepsTimer.current) {
        clearTimeout(pendingRepsTimer.current);
        pendingRepsTimer.current = null;
      }
    };
    // Deliberately NOT keyed on `applyingReps`: re-running when it flips false
    // would re-schedule a still-dirty change straight after a HARD failure —
    // an endless retry + toast loop. A change made mid-apply is instead caught
    // by the next edit or by the commit-before-leave in attemptLeave, so it is
    // delayed at worst, never lost. (The transient 409-while-generating case has
    // its own bounded retry inside applySalesRepChanges.)
  }, [draft.aiEmployeeAssignments, draft.emailAccountsByAie, draft.linkedinAccountIdsByAie, loaded]);

  const stepContent = () => {
    const step = STEPS[currentStep]!;
    switch (step.key) {
      case 'type': {
        // Edit mode auto-applies rep / sender-account changes (the debounced
        // effect above) — no "Apply" button to forget. The ONE change that
        // cannot auto-apply is removing a sender account from a LAUNCHED
        // campaign: it would strand the leads already sending from it. Say so
        // inline, right where the pick was made, instead of silently dropping it.
        const launchedBlockedRemoval =
          isEditingNonDraft &&
          !!draft.launchedAt &&
          !hasRepSetChange(appliedAssignments.current, draft.aiEmployeeAssignments) &&
          senderAccountApplyAction(
            appliedEmailAccountsByAie.current,
            draft.emailAccountsByAie,
            appliedLinkedinAccountIdsByAie.current,
            draft.linkedinAccountIdsByAie,
          ) === 'blocked-removal';
        return html`
          ${
            launchedBlockedRemoval
              ? html`
                <div class="alert alert-warning mb-4 flex items-start gap-2">
                  <span class="icon-[material-symbols--info] mt-0.5 text-lg"></span>
                  <div class="text-sm">
                    A sender account can't be removed from a launched campaign — the leads already sending from it would
                    be stranded, so this change won't be saved. Re-tick the account; you can still ADD one.
                  </div>
                </div>
              `
              : nothing
          }
          ${WizCampaignType({
            emailAccountsByAie: draft.emailAccountsByAie,
            linkedinAccountIdsByAie: draft.linkedinAccountIdsByAie,
            aiEmployeeAssignments: draft.aiEmployeeAssignments,
            // Channels the sequence sends on — the step marks a rep whose
            // account picker for one of them is still empty, right where it
            // can be fixed, instead of failing on Apply.
            requiredChannels: requiredChannels(),
            // A rep covered by a Summary-step fallback borrows someone else's
            // account for that channel, so the hint has to stay quiet for them
            // — same rule the server validates by.
            channelFallbacks: draft.channelFallbacks ?? [],
            // Keep the per-rep "missing account" hints hidden until the user
            // tries to move on (set in goToStep). Reads as an error the instant
            // a rep is ticked otherwise — the exact thing UAT flagged 2026-09-19.
            revealAccountGaps: revealRepGaps,
            // See WizCampaignTypeProps.assignmentsReady: a brand-new draft has
            // nothing to wait for, but an existing campaign's real assignment
            // only becomes trustworthy once the load effect above has finished.
            assignmentsReady: !campaignId || loaded,
            crmAutoSync: draft.crmAutoSync ?? false,
            onCrmAutoSyncChange: enabled => setDraft({ ...draft, crmAutoSync: enabled }),
            onAssignmentsChange: applyAssignmentsChange,
            onEmailAccountsByAieChange: emailAccountsByAie =>
              setDraft({
                ...draft,
                emailAccountsByAie,
                // Flat list = union across AIEs (used by legacy validators / API).
                emailAccounts: Array.from(new Set(Object.values(emailAccountsByAie).flat())),
              }),
            onLinkedinAccountIdsByAieChange: linkedinAccountIdsByAie =>
              setDraft({
                ...draft,
                linkedinAccountIdsByAie,
                linkedinAccountIds: Array.from(new Set(Object.values(linkedinAccountIdsByAie).flat())),
              }),
          })}
        `;
      }
      case 'offering':
        return WizOffering({
          offering: draft.offering,
          onOfferingChange: handleOfferingChange,
          onBusyChange: setOfferingBusy,
          uploadState: offeringUploadState,
          onUploadStateChange: setOfferingUploadState,
          parsedDocs: offeringParsedDocs,
          onParsedDocsChange: setOfferingParsedDocs,
        });
      case 'leads':
        // Leads already exist for this campaign (real documents with
        // generated messages) — the one-shot CSV/Apollo import tool below
        // (WizLeads) can't safely edit that list, so manage them through the
        // dedicated add/remove endpoints instead. Works during the launch
        // cool-down; every change restarts the hour.
        if (isEditingNonDraft) {
          return WizManageLeads({
            campaignId: draft._id!,
            hasApolloSearch: !!draft.apolloSearchInput,
            leads: draft.leads as any,
            onLeadsChange: leads => setDraft({ ...liveDraft.current, leads: leads as any }),
          });
        }
        return WizLeads({
          campaignName: draft.name,
          onCampaignNameChange: name => setDraft({ ...draft, name }),
          leads: draft.leads,
          onLeadsChange: leads => setDraft({ ...draft, leads }),
          // Derived from the current sequence — the user no longer picks a type.
          campaignType: deriveCampaignType(draft.sequence),
          apolloSearchInput: draft.apolloSearchInput,
          onApolloSearchInputChange: apolloSearchInput => setDraft({ ...draft, apolloSearchInput }),
          projectId: draft.projectId,
          onProjectIdChange: projectId => setDraft({ ...draft, projectId }),
        });
      case 'schedule':
        return WizSchedule({
          schedule: draft.schedule,
          sequence: draft.sequence,
          onScheduleChange: schedule => setDraft({ ...draft, schedule }),
        });
      case 'sequence':
        // The same notice the connect step carries, once at the top of the
        // step as well — asked for after the eleven-day outage, because the
        // sequence editor is where the note is written and therefore the last
        // place the person can still act on what will happen to it. It renders
        // nothing at all unless there is something true to say, so a campaign
        // with no connect step and a fully paid set of seats sees no banner.
        return html`
          ${ConnectionNoteNotice({
          hasNoteOnConnectStep: draft.sequence.some(s => connectStepCarriesANote(s)),
          // Only the accounts this draft sends from. The flat list rather than
          // the per-rep map: the notice names accounts, and it does not care
          // which rep each one belongs to.
          sendingAccountIds: draft.linkedinAccountIds,
        })}
          ${WizSequence({
          sequence: draft.sequence,
          linkedinAccountIds: draft.linkedinAccountIds,
          previewAiEmployeeId: previewSenderId(draft),
          leads: draft.leads,
          offering: draft.offering,
          onSequenceChange: handleSequenceChange,
          autoReply: draft.autoReply ?? false,
          onAutoReplyChange: enabled => setDraft({ ...draft, autoReply: enabled }),
          replyPrompt: draft.replyPrompt ?? '',
          onReplyPromptChange: prompt => setDraft({ ...draft, replyPrompt: prompt }),
          // Not "is there any account in the draft" but "is every rep that sends
          // on this channel covered" — the flat lists miss a rep who borrows the
          // channel through a fallback, and the banner then contradicted the
          // navigation gate right next to it.
          hasEmailAccount: !salesRepAccountGaps().email,
          hasLinkedinAccount: !salesRepAccountGaps().linkedin,
          apolloSearchInput: draft.apolloSearchInput,
          language: draft.language ?? 'en',
          onLanguageChange: async language => {
            // ONE atomic update: set the language AND drop the stored AI step
            // previews (they are in the old language). AI cards regenerate
            // immediately — in the new language, honoring any refine prompt.
            setDraft({
              ...liveDraft.current,
              language,
              sequence: liveDraft.current.sequence.map(s => ({
                ...s,
                previewSubject: undefined,
                previewBody: undefined,
                previewKey: undefined,
              })),
            });
            // MANUAL messages are sent verbatim (never AI-generated), so they are
            // NOT regenerated. Instead we translate the exact text into the new
            // language once per explicit language switch and store it back as the
            // manual body/subject. {{variables}} are preserved server-side. This
            // keeps both the collapsed and expanded views (which read manualBody)
            // consistently in the selected language.
            const manualSteps = liveDraft.current.sequence.filter(s => s.manualBody?.trim());
            // Tell the operator up front: translating a manual message is the one
            // AI touch on copy they wrote by hand, and it replaces that text.
            // Shown only when there is manual copy to translate.
            if (manualSteps.length > 0) {
              toast.info('Changing languages will translate your manually written messages');
            }
            let translationFailures = 0;
            for (const ms of manualSteps) {
              try {
                const resp = await fetch('/api/prompt/translate', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ body: ms.manualBody, subject: ms.subject, channel: ms.channel, language }),
                });
                const data = (await resp.json().catch(() => null)) as {
                  ok: boolean;
                  body?: string;
                  subject?: string;
                } | null;
                if (data?.ok && data.body) {
                  setDraft({
                    ...liveDraft.current,
                    sequence: liveDraft.current.sequence.map(s =>
                      s.id === ms.id ? { ...s, manualBody: data.body, subject: data.subject ?? s.subject } : s,
                    ),
                  });
                } else {
                  translationFailures++;
                }
              } catch {
                translationFailures++;
              }
            }
            // A failed translation must NOT be silent — that is exactly the
            // "German campaign, English message" blocker. The manual copy stays
            // in the previous language; say so, so the operator fixes it before
            // launch rather than discovering it in a prospect's inbox.
            if (translationFailures > 0) {
              const one = translationFailures === 1;
              toast.error(
                `Couldn't translate ${translationFailures} manual message${one ? '' : 's'} — ` +
                  `${one ? 'it stays' : 'they stay'} in the previous language. Edit ${one ? 'it' : 'them'} before launching.`,
              );
            }
          },
        })}
        `;
      case 'summary':
        // Editing an already-prepared campaign: there's nothing left to
        // "generate from scratch" — every change on the way here (offering,
        // schedule, step content, new steps, Sales Rep) was already saved
        // incrementally through its own safe endpoint. Calling
        // /api/campaign/prepare here would just 409 (it now refuses anything
        // past 'draft' — see fix/launch-cooldown-bugs) and would be
        // destructive if it somehow didn't. Just confirm and return.
        if (isEditingNonDraft) {
          return html`
            <div class="card bg-base-100 shadow-sm">
              <div class="card-body items-center gap-3 py-10 text-center">
                <span class="icon-[material-symbols--check-circle] text-success text-4xl"></span>
                <h2 class="card-title text-lg">Changes saved</h2>
                <p class="text-base-content/60 max-w-md text-sm">
                  Everything you changed (offering, schedule, sequence, Sales Rep) has already been saved
                  automatically. Return to the campaign to review the generated messages.
                </p>
                <button
                  class="btn btn-primary mt-2 gap-1.5"
                  @click=${() => {
                    setFinished(true);
                    wizardBeforeLeave = null;
                    sdrRouter.goTo('campaignDetail', { params: { campaignId: draft._id } } as any);
                  }}
                >
                  <span class="icon-[material-symbols--arrow-back] text-base"></span>
                  Back to Campaign
                </button>
              </div>
            </div>
          `;
        }
        return WizSummary({
          draft,
          onPrepared: (campaignId: string) => {
            // Mark as finished so the wizard does not try to save the draft on leave
            // and skip the beforeleave guard. Then jump to the campaign detail page
            // where the user reviews/edits messages and finally launches the campaign.
            setFinished(true);
            wizardBeforeLeave = null;
            sdrRouter.goTo('campaignDetail', { params: { campaignId } } as any);
          },
        });
    }
  };

  const blockReason = forwardBlockReason();
  // The Sales Rep account gate answers on click instead of greying "Next" out
  // and restating the rep picker's own hint one size louder underneath it.
  // See SALES_REP_ACCOUNT_BLOCK.
  const softBlocked = blockReason === SALES_REP_ACCOUNT_BLOCK;
  // Furthest step the user may jump to: walk forward from the current step and
  // stop at the first one whose required fields aren't satisfied yet. The user
  // can still land ON that step (to fill it) but not skip past it — so the
  // stepper dots beyond it are disabled.
  let forwardLimit = currentStep;
  while (forwardLimit < STEPS.length - 1 && !stepBlockReason(STEPS[forwardLimit]?.key)) {
    forwardLimit++;
  }

  return html`
    <div class="mx-auto flex max-w-4xl flex-col gap-6 p-6">
      <div>
        <button
          class="btn btn-ghost btn-sm gap-1"
          @click=${() => {
            sdrRouter.goTo('dashboard');
          }}
        >
          <span class="icon-[material-symbols--arrow-back]"></span> Back to Dashboard
        </button>
        <h1 class="mt-2 text-3xl font-bold">${campaignId ? 'Edit Campaign' : 'New Campaign'}</h1>
        <div class="flex flex-wrap items-center gap-3">
          <p class="text-base-content/70">Follow the steps to configure and launch your outreach.</p>
          <div class="flex items-center gap-2">
            ${
              saving
                ? html`<span class="text-base-content/50 inline-flex items-center gap-1 text-xs italic">
                  <span class="loading loading-spinner loading-xs"></span> Saving…
                </span>`
                : saveError
                  ? html`<span class="text-error inline-flex items-center gap-1 text-xs" title=${saveError}>
                    <span class="icon-[material-symbols--cloud-off] text-sm"></span> Save failed — use Save to retry
                  </span>`
                  : lastSavedAt
                    ? html`<span class="text-success inline-flex items-center gap-1 text-xs">
                      <span class="icon-[material-symbols--cloud-done] text-sm"></span> All changes saved
                    </span>`
                    : html`<span class="text-base-content/40 text-xs">Not saved yet</span>`
            }
            ${
              // One quiet, in-place signal that edits will regenerate on exit —
              // replaces the per-change toasts that used to stack up. The single
              // bundled toast still fires on leave (flushPendingRegeneration).
              regenPending
                ? html`<span class="text-info inline-flex items-center gap-1 text-xs">
                  <span class="icon-[material-symbols--autorenew] text-sm"></span> Messages will regenerate when you leave
                </span>`
                : nothing
            }
            <button
              class="btn btn-outline btn-xs gap-1"
              ?disabled=${saving}
              title="Save your progress now"
              @click=${() => void persistDraft(draft)}
            >
              <span class="icon-[material-symbols--save] text-sm"></span> Save
            </button>
          </div>
        </div>
      </div>

      <!-- Stepper -->
      <div class="flex items-center gap-1">
        ${STEPS.map((step, i) => {
          const complete = isStepComplete(step.key, draft, finished) && (i <= maxStepReached || finished);
          return html`
            ${
              i > 0
                ? html`<div
                  class="${
                    isStepComplete(STEPS[i - 1]!.key, draft, finished) && (i - 1 < maxStepReached || finished)
                      ? 'bg-success'
                      : i <= currentStep
                        ? 'bg-primary'
                        : 'bg-base-300'
                  } h-0.5 flex-1"
                ></div>`
                : ''
            }
            <button
              ?disabled=${offeringBusy || i > forwardLimit}
              title=${
                offeringBusy
                  ? 'Please wait until processing finishes'
                  : i > forwardLimit
                    ? (stepBlockReason(STEPS[forwardLimit]?.key) ?? 'Complete the required fields to continue')
                    : ''
              }
              class="${
                i === currentStep
                  ? complete
                    ? 'bg-success text-success-content shadow-sm'
                    : 'bg-primary text-primary-content shadow-sm'
                  : complete
                    ? 'bg-success/10 text-success hover:bg-success/20'
                    : 'bg-base-200 text-base-content/50 hover:bg-base-300'
              } flex cursor-pointer items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50"
              @click=${() => void goToStep(i)}
            >
              ${
                complete
                  ? html`<span class="icon-[material-symbols--check-circle] text-base"></span>`
                  : html`<span class="icon-[material-symbols--${step.icon}] text-base"></span>`
              }
              <span class="hidden sm:inline">${step.label}</span>
              <span class="sm:hidden">${i + 1}</span>
            </button>
          `;
        })}
      </div>

      <!-- Step content -->
      ${
        campaignId && !loaded
          ? html`<div class="flex items-center justify-center py-20">
            <span class="loading loading-spinner loading-lg"></span>
          </div>`
          : keyed(currentStep, html`<div class="ndi-step-enter">${stepContent()}</div>`)
      }
      ${
        offeringBusy
          ? html`<div class="alert alert-info py-2 text-sm">
            <span class="loading loading-spinner loading-xs"></span>
            <span
              >Processing your document/website — you can continue once it's finished so the result isn't lost.</span
            >
          </div>`
          : blockReason && !softBlocked && canGoForward
            ? html`<div class="alert alert-warning py-2 text-sm">
              <span class="icon-[material-symbols--info] text-lg"></span>
              <span>${blockReason}</span>
            </div>`
            : nothing
      }

      <!-- Navigation -->
      <div class="flex items-center justify-between">
        <button
          class="btn btn-ghost ${canGoBack ? '' : 'invisible'} gap-1"
          ?disabled=${offeringBusy}
          title=${offeringBusy ? 'Please wait until the document/website finishes processing.' : ''}
          @click=${() => void goToStep(currentStep - 1)}
        >
          <span class="icon-[material-symbols--arrow-back]"></span>
          ${STEPS[currentStep - 1]?.label ?? ''}
        </button>
        ${
          canGoForward
            ? html`
              <button
                class="btn btn-primary gap-1"
                ?disabled=${!!blockReason && !softBlocked}
                title=${blockReason ?? ''}
                @click=${() => void goToStep(currentStep + 1)}
              >
                ${STEPS[currentStep + 1]?.label ?? ''}
                <span class="icon-[material-symbols--arrow-forward]"></span>
              </button>
            `
            : nothing
        }
      </div>
    </div>
  `;
});
